import "./Checkbox.scss";

import type { ChangeEvent, FC, InputHTMLAttributes, MouseEventHandler, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { useControllable } from "mds/hooks/use-controllable";
import { CheckIcon } from "mds/icons";
import { ComponentEventProperties, addTrackingToEvent } from "utils/amplitude";

interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange">,
    ComponentEventProperties {
  children?: ReactNode;
  initialChecked?: boolean;
  onChange?: (newChecked: boolean, event: ChangeEvent<HTMLInputElement>) => void;
  onContainerClick?: MouseEventHandler<HTMLDivElement>;
}

export const Checkbox: FC<CheckboxProps> = ({
  children,
  className,
  initialChecked,
  checked: controlledCheck,
  onChange,
  onContainerClick,
  eventName,
  eventProperties,
  ...props
}) => {
  const [checked, setChecked] = useControllable<boolean, ChangeEvent<HTMLInputElement>>(
    controlledCheck,
    initialChecked ?? false,
    onChange,
  );
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => setChecked(!checked, event);
  return (
    <div className={twMerge("checkbox flex", className)} onClick={onContainerClick}>
      <label className={twMerge("flex items-center gap-2", className)}>
        <input
          checked={checked}
          type="checkbox"
          onChange={addTrackingToEvent({ eventName, eventProperties, onEvent: handleChange })}
          {...props}
        />
        <div>{checked && <CheckIcon className="check-icon text-white" />}</div>
        {children}
      </label>
    </div>
  );
};

Checkbox.displayName = "Checkbox";
